import { supabase } from "@/lib/supabase";

/**
 * Indique si une autre transaction que `excludeTransactionId` a appel_en_cours sur ce dossier.
 */
export async function fetchDossierCallBlockedByOthers(dossierId, excludeTransactionId) {
  if (!dossierId) return false;
  const { data, error } = await supabase
    .from("transactions")
    .select("id, appel_en_cours")
    .eq("dossier_id", dossierId);
  if (error || !data?.length) return false;
  return data.some((row) => {
    const active = row.appel_en_cours === true || row.appel_en_cours === "true" || row.appel_en_cours === 1;
    if (!active) return false;
    const rid = String(row.id ?? row.transaction_id ?? "");
    const ex = excludeTransactionId != null ? String(excludeTransactionId) : "";
    if (!ex) return true;
    return rid !== ex;
  });
}

/** Toute transaction du dossier a appel_en_cours (ex. dashboard : ligne occupée). */
export async function fetchDossierAnyCallInProgress(dossierId) {
  if (!dossierId) return false;
  const { data, error } = await supabase
    .from("transactions")
    .select("id, appel_en_cours")
    .eq("dossier_id", dossierId)
    .limit(50);
  if (error || !data?.length) return false;
  return data.some(
    (row) => row.appel_en_cours === true || row.appel_en_cours === "true" || row.appel_en_cours === 1
  );
}

/**
 * Souscrit aux changements sur transactions pour un dossier. Met à jour busy via callback.
 * @param {object} [options]
 * @param {string} [options.uniqueSuffix] — évite de réutiliser le même nom de canal qu’un autre composant (ex. Dashboard + BuyerCallSession) : sinon Supabase lève « cannot add postgres_changes … after subscribe() ».
 * @returns {() => void} cleanup
 */
export function subscribeDossierCallLock(dossierId, onBusyChange, options = {}) {
  const { excludeTransactionId = null, anyInProgress = false, uniqueSuffix = "" } = options;

  const refresh = async () => {
    const busy = anyInProgress
      ? await fetchDossierAnyCallInProgress(dossierId)
      : await fetchDossierCallBlockedByOthers(dossierId, excludeTransactionId);
    onBusyChange(busy);
  };

  refresh();

  const topic =
    uniqueSuffix && String(uniqueSuffix).trim()
      ? `dossier-${dossierId}-${String(uniqueSuffix).trim()}`
      : `dossier-${dossierId}`;

  const channel = supabase
    .channel(topic)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "transactions",
        filter: `dossier_id=eq.${dossierId}`,
      },
      () => {
        refresh();
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
